import { create } from '@bufbuild/protobuf';
import {
  ClientMessageSchema,
  type ClientMessage,
} from '../protocol/gen/warinspace/v2/client_to_server_pb.js';
import {
  BotDifficulty,
  DebugCommandType,
  LobbyVisibility,
  Team,
  TeamPreference,
  UpgradePath,
} from '../protocol/gen/warinspace/v2/common_pb.js';

export const Outbound = {
  moveTo(x: number, y: number): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'moveTo', value: { x, y } } });
  },

  setRotation(angleRadians: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'setRotation', value: { angleRadians } },
    });
  },

  upgrade(path: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'upgrade', value: { path: pathOf(path) } },
    });
  },

  evolveShip(path: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'evolveShip', value: { path: pathOf(path) } },
    });
  },

  joinGame(team: number, name: string): ClientMessage {
    const teamPreference =
      team === 0 ? TeamPreference.BLUE : team === 1 ? TeamPreference.RED : TeamPreference.AUTO;
    return create(ClientMessageSchema, {
      payload: { case: 'joinGame', value: { teamPreference, name } },
    });
  },

  ping(clientNonce: bigint): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'ping', value: { clientNonce } } });
  },

  nameRequest(nameId: number): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'nameRequest', value: { nameId } } });
  },

  useActiveAtPosition(x: number, y: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'useActiveAtPosition', value: { x, y } },
    });
  },

  useActiveOnPlayer(targetId: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'useActiveOnPlayer', value: { targetId } },
    });
  },

  fireAtTarget(targetId: number, x: number, y: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'fireAtTarget', value: { targetId, x, y } },
    });
  },

  fireMiss(x: number, y: number): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'fireMiss', value: { x, y } } });
  },

  suicide(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'suicide', value: {} } });
  },

  queryWorlds(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'queryWorlds', value: {} } });
  },

  sendEmoji(emojiIndex: number): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'sendEmoji', value: { emojiIndex } } });
  },

  refreshAccount(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'refreshAccount', value: {} } });
  },

  changeWorld(serverNum: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'changeWorld', value: { serverNum } },
    });
  },

  notPlayingNextRound(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'notPlayingNextRound', value: {} } });
  },

  createLobby(visibility: LobbyVisibility, name: string): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'createLobby', value: { visibility, name } },
    });
  },

  joinLobby(joinCode: string, spectate: boolean): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'joinLobby', value: { joinCode, spectate } },
    });
  },

  leaveLobby(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'leaveLobby', value: {} } });
  },

  cancelLobbyStart(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'cancelLobbyStart', value: {} } });
  },

  startLobby(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'startLobby', value: {} } });
  },

  setTeamDifficulty(team: Team, difficulty: BotDifficulty): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'setTeamDifficulty', value: { team, difficulty } },
    });
  },

  queryLobbies(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'queryLobbies', value: {} } });
  },

  takeLobbySeat(slotId: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'takeLobbySeat', value: { slotId } },
    });
  },

  spectateLobby(): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'spectateLobby', value: {} } });
  },

  sendChat(text: string): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'chat', value: { text } } });
  },

  setFeatureOverrides(overrides: Record<string, boolean>): ClientMessage {
    return create(ClientMessageSchema, {
      payload: {
        case: 'setFeatureOverrides',
        value: {
          allOverrides: Object.entries(overrides).map(([key, enabled]) => ({ key, enabled })),
        },
      },
    });
  },

  debugCommand(type: DebugCommandType, amount: number): ClientMessage {
    return create(ClientMessageSchema, {
      payload: { case: 'debugCommand', value: { type, amount } },
    });
  },

  surrenderVote(agree: boolean): ClientMessage {
    return create(ClientMessageSchema, { payload: { case: 'surrenderVote', value: { agree } } });
  },
} as const;

/** Anything outside 0-2 maps to `UNSPECIFIED` rather than a wrapped index, so the server declines it instead of spending scrap on a path nobody chose. */
function pathOf(path: number): UpgradePath {
  switch (path) {
    case 0:
      return UpgradePath.ZERO;
    case 1:
      return UpgradePath.ONE;
    case 2:
      return UpgradePath.TWO;
    default:
      return UpgradePath.UNSPECIFIED;
  }
}
